import { useState } from 'react';
import { useProject } from '../lib/project-context';
import { useDecisions } from '../lib/queries';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Zap, ChevronDown, ChevronUp, LineChart, TrendingUp, HardHat,
  ArrowRight, ShieldAlert, DollarSign, Activity, Info, BarChart3,
  Settings2
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const CATEGORY_CONFIG = {
  financial: { icon: LineChart, color: 'var(--financial-primary)', label: 'Financiero', path: '/analyzer', action: 'Ajustar escenario' },
  market: { icon: TrendingUp, color: 'var(--market-primary)', label: 'Mercado', path: '/market', action: 'Ver mercado' },
  leverage: { icon: BarChart3, color: 'var(--construction-primary)', label: 'Apalancamiento', path: '/analyzer', action: 'Ajustar escenario' },
  pricing: { icon: DollarSign, color: 'var(--market-primary)', label: 'Precio', path: '/market', action: 'Ver comparables' },
  absorption: { icon: Activity, color: 'var(--market-primary)', label: 'Absorción', path: '/market', action: 'Ver mercado' },
  risk: { icon: ShieldAlert, color: 'var(--danger)', label: 'Riesgo', path: '/analyzer', action: 'Revisar inputs' },
  construction: { icon: HardHat, color: 'var(--construction-primary)', label: 'Construcción', path: '/construction', action: 'Ver obra' },
};

const PRIORITY_STYLE = {
  high: { color: '#DF1B41', bg: 'rgba(223,27,65,0.06)', border: 'rgba(223,27,65,0.18)', accent: '#DF1B41', label: 'Alta' },
  medium: { color: '#C87A1A', bg: 'rgba(245,166,35,0.06)', border: 'rgba(245,166,35,0.18)', accent: '#F5A623', label: 'Media' },
  low: { color: '#0A8754', bg: 'rgba(12,191,115,0.06)', border: 'rgba(12,191,115,0.18)', accent: '#0CBF73', label: 'Baja' },
};

const IMPACT_DIMS = [
  { key: 'tir_impact', label: 'TIR', color: 'var(--financial-primary)' },
  { key: 'risk_impact', label: 'Riesgo', color: 'var(--danger)' },
  { key: 'absorption_impact', label: 'Absorción', color: 'var(--market-primary)' },
  { key: 'consistency_impact', label: 'Consistencia', color: 'var(--construction-primary)' },
];

/* ── Inline impact mini-bars (always visible, no expand needed) ── */
function ImpactMini({ impact }) {
  if (!impact) return null;
  const dims = IMPACT_DIMS.filter(d => (impact[d.key] || 0) > 10);
  if (!dims.length) return null;
  return (
    <div className="flex items-center gap-1.5" data-testid="impact-mini">
      {dims.map(d => (
        <div key={d.key} className="flex items-center gap-1" title={`${d.label}: ${impact[d.key]}`}>
          <div className="w-[38px] h-[4px] rounded-full" style={{ background: 'var(--border-subtle)' }}>
            <div className="h-full rounded-full" style={{ width: `${Math.min(100, impact[d.key])}%`, background: d.color }} />
          </div>
          <span className="text-[8px] font-semibold tabular-nums" style={{ color: d.color }}>{impact[d.key]}</span>
        </div>
      ))}
    </div>
  );
}

/* ── Single decision card — visual-first, scannable ── */
function DecisionCard({ decision, index, isExpanded, onToggle, projectId, scenarioId }) {
  const navigate = useNavigate();
  const cat = CATEGORY_CONFIG[decision.category] || CATEGORY_CONFIG.financial;
  const prio = PRIORITY_STYLE[decision.priority] || PRIORITY_STYLE.medium;
  const CatIcon = cat.icon;
  const impact = decision.impact || {};
  const rank = decision.rank || index + 1;

  const actionTarget = decision.action_target; // wizard step id or null
  const hasAction = !!actionTarget && projectId && scenarioId;

  const handleAction = (e) => {
    e.stopPropagation();
    if (hasAction) {
      navigate(`/analyzer/wizard/${projectId}/${scenarioId}?step=${actionTarget}`);
    }
  };

  return (
    <div
      className="group rounded-xl overflow-hidden transition-all duration-200"
      style={{ background: 'var(--bg-secondary)', border: `1px solid ${isExpanded ? prio.border : 'var(--border-default)'}` }}
      data-testid={`decision-card-${index}`}
    >
      {/* ── Main row: always visible, scannable ── */}
      <div className="flex items-stretch">
        {/* Priority accent bar */}
        <div className="w-1 shrink-0" style={{ background: prio.accent }} />

        <div className="flex-1 min-w-0 p-3.5 flex items-start gap-3">
          {/* Rank circle */}
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
            style={{ background: prio.bg, border: `1px solid ${prio.border}` }}
          >
            <span className="text-xs font-bold" style={{ color: prio.color }}>{rank}</span>
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 mb-1 flex-wrap">
              <CatIcon className="w-3 h-3 shrink-0" style={{ color: cat.color }} />
              <span className="text-[11px] font-semibold leading-tight truncate" style={{ color: 'var(--text-primary)' }}>
                {decision.title}
              </span>
              <Badge className="text-[7px] px-1 py-0 h-3.5 shrink-0 rounded" style={{ background: prio.bg, color: prio.color, border: `1px solid ${prio.border}` }}>
                {prio.label}
              </Badge>
            </div>

            {/* Impact summary — one line visible */}
            <p className="text-[10px] leading-relaxed mb-1.5 line-clamp-1" style={{ color: 'var(--text-secondary)' }}>
              {decision.expected_impact}
            </p>

            {/* Mini impact bars */}
            <ImpactMini impact={impact} />
          </div>

          {/* Actions column */}
          <div className="flex flex-col items-end gap-1.5 shrink-0 ml-2">
            {hasAction && (
              <Button
                size="sm"
                className="h-7 text-[10px] gap-1 px-2.5 rounded-lg"
                style={{ background: cat.color, color: '#fff', border: 'none' }}
                onClick={handleAction}
                data-testid={`decision-action-${index}`}
              >
                <Settings2 className="w-3 h-3" />
                Aplicar ajuste
              </Button>
            )}
            <button
              onClick={(e) => { e.stopPropagation(); onToggle(); }}
              className="flex items-center gap-0.5 text-[9px] font-medium hover:underline"
              style={{ color: 'var(--text-muted)' }}
              data-testid={`decision-toggle-${index}`}
            >
              {isExpanded ? 'Menos' : 'Detalle'}
              {isExpanded ? <ChevronUp className="w-2.5 h-2.5" /> : <ChevronDown className="w-2.5 h-2.5" />}
            </button>
          </div>
        </div>
      </div>

      {/* ── Expanded detail (optional) ── */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <div className="px-4 pb-4 pt-0 ml-[4px]" style={{ borderTop: `1px solid ${prio.border}` }}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-3">
                {/* Impact bars full */}
                <div className="rounded-lg p-3 space-y-2" style={{ background: 'var(--bg-primary)', border: '1px solid var(--border-subtle)' }} data-testid={`impact-bars-${index}`}>
                  <div className="text-[9px] font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>Impacto por dimensión</div>
                  {IMPACT_DIMS.map(d => {
                    const val = impact[d.key] || 0;
                    if (val <= 0) return null;
                    return (
                      <div key={d.key} className="flex items-center gap-2" data-testid={`impact-${d.key}`}>
                        <span className="text-[9px] font-medium w-16 text-right shrink-0" style={{ color: 'var(--text-muted)' }}>{d.label}</span>
                        <div className="flex-1 h-1.5 rounded-full" style={{ background: 'var(--border-subtle)' }}>
                          <div className="h-full rounded-full transition-all duration-500" style={{ width: `${val}%`, background: d.color }} />
                        </div>
                        <span className="text-[9px] font-bold tabular-nums w-6 shrink-0" style={{ color: d.color }}>{val}</span>
                      </div>
                    );
                  })}
                </div>

                {/* Rationale */}
                <div className="rounded-lg p-3" style={{ background: 'var(--bg-primary)', border: '1px solid var(--border-subtle)' }}>
                  <div className="text-[9px] font-semibold uppercase tracking-wider mb-1.5" style={{ color: 'var(--text-muted)' }}>Fundamento</div>
                  <p className="text-[10px] leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
                    {decision.rationale}
                  </p>
                </div>
              </div>

              {/* Suggested action */}
              <div className="rounded-lg p-3 mt-2" style={{ background: cat.color + '08', border: `1px solid ${cat.color}18` }}>
                <div className="text-[9px] font-semibold uppercase tracking-wider mb-1" style={{ color: cat.color }}>Acción sugerida</div>
                <p className="text-[10px] leading-relaxed" style={{ color: 'var(--text-primary)' }}>
                  {decision.suggested_action}
                </p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ── Main DecisionEngine component ── */
export function DecisionEngine({ compact = false }) {
  const { activeProject, activeScenario } = useProject();
  const [expanded, setExpanded] = useState(null);
  const { data, isLoading } = useDecisions(activeProject?.id, activeScenario?.id);

  if (isLoading && !data) return null;
  if (!data || !data.decisions?.length) return null;

  const dq = data.data_quality;
  const diag = data.diagnosis;

  return (
    <Card className="rounded-xl border-border/50 shadow-sm" data-testid="decision-engine">
      <CardContent className={compact ? 'p-4' : 'p-5'}>
        {/* Header */}
        <div className="flex items-center gap-2 mb-4">
          <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: 'var(--accent-subtle)' }}>
            <Zap className="w-4 h-4" style={{ color: 'var(--accent-primary)' }} />
          </div>
          <h3 className="text-xs font-display font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
            Decision Engine
          </h3>
          {diag?.risks?.length > 0 && (
            <Badge className="ml-auto text-[9px]" style={{ background: 'var(--danger-soft)', color: 'var(--danger)', border: 'none' }} data-testid="decision-risk-count">
              {diag.risks.length} riesgo{diag.risks.length > 1 ? 's' : ''}
            </Badge>
          )}
        </div>

        {/* Data quality disclaimer */}
        {dq?.has_limitations && (
          <div className="flex items-start gap-2 rounded-lg p-2.5 mb-3" style={{ background: 'rgba(245,166,35,0.05)', border: '1px solid rgba(245,166,35,0.12)' }} data-testid="decision-data-quality">
            <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" style={{ color: 'var(--warning-color)' }} />
            <p className="text-[10px] leading-relaxed" style={{ color: 'var(--text-secondary)' }}>{dq.message}</p>
          </div>
        )}

        {/* Decision cards */}
        <div className="space-y-2">
          {data.decisions.map((d, i) => (
            <DecisionCard
              key={d.id || i}
              decision={d}
              index={i}
              isExpanded={expanded === i}
              onToggle={() => setExpanded(expanded === i ? null : i)}
              projectId={activeProject?.id}
              scenarioId={activeScenario?.id}
            />
          ))}
        </div>

        {/* Critical variables */}
        {!compact && diag?.critical_variables?.length > 0 && (
          <div className="mt-4 pt-3" style={{ borderTop: '1px solid var(--border-subtle)' }}>
            <div className="text-[10px] font-semibold uppercase tracking-wider mb-2" style={{ color: 'var(--text-muted)' }}>Variables críticas</div>
            <div className="flex flex-wrap gap-1.5">
              {diag.critical_variables.map((v, i) => (
                <span key={i} className="text-[10px] px-2 py-1 rounded-md" style={{ background: 'var(--bg-primary)', color: 'var(--text-secondary)', border: '1px solid var(--border-subtle)' }} data-testid={`critical-var-${i}`}>
                  {v.variable}
                </span>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
